import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Search, FileText, Users, type LucideIcon } from 'lucide-react';
import { useUserStore } from '../../stores';
import { usePodsStore } from '../../stores/usePodsStore';
import './BottomNav.css';

export type NavItem = 'today' | 'explore' | 'plan' | 'pods';

const navIcons: Record<NavItem, LucideIcon> = {
  today: Clock,
  explore: Search,
  plan: FileText,
  pods: Users,
};

interface NavItemConfig {
  id: NavItem;
  label: string;
  path: string;
}

// Spec §22.5 (Phase 4) renamed Today to **Now**. Owner decision 2b (fidelity
// plan, 2026-09-28) puts it first and supersedes §22.5's order: Now, then
// Explore, then Plan, then Pods when the user has one. Icon tabs; the landing
// route does not change (`/` is still Now, and contextual landing still decides).
//
// The `id` and the `path` deliberately still say `today` and `/`. The rename is
// user-facing; route paths stay stable by spec, and churning the id through the
// eight files that pass `activeItem` would be cost without user-visible gain —
// the same call this codebase already documents for mobile's `plans/` directory.
const navItems: NavItemConfig[] = [
  { id: 'today', label: 'Now', path: '/' },
  { id: 'explore', label: 'Explore', path: '/explore' },
  { id: 'plan', label: 'Plan', path: '/plan' },
  { id: 'pods', label: 'Pods', path: '/pods' },
];

interface BottomNavProps {
  activeItem?: NavItem;
  onItemClick?: (item: NavItem) => void | boolean;
}

export const BottomNav: React.FC<BottomNavProps> = ({
  activeItem = 'today',
  onItemClick,
}) => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const { pods, fetchPods } = usePodsStore();

  // The Pods tab earns its nav slot only once the user has (or was invited
  // to) a pod — three tabs until then. Store caches for 5 minutes.
  useEffect(() => {
    if (user) fetchPods().catch(() => {});
  }, [user, fetchPods]);
  const showPods = !!user && (pods.length > 0 || activeItem === 'pods');

  const visibleItems = navItems.filter(
    (item) => item.id !== 'pods' || showPods
  );

  const handleClick = (item: NavItemConfig) => {
    if (onItemClick) {
      const preventDefault = onItemClick(item.id);
      if (preventDefault) return;
    }
    navigate(item.path);
  };

  return (
    <nav className="bottom-nav" role="navigation" aria-label="Main navigation">
      {visibleItems.map((item) => {
        const isActive = activeItem === item.id;
        const Icon = navIcons[item.id];
        return (
          <button
            key={item.id}
            className={`nav-item ${isActive ? 'active' : ''}`}
            onClick={() => handleClick(item)}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={22} />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNav;
